/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react/function-component-definition */
/* eslint-disable @typescript-eslint/no-use-before-define */

import SpeakerAvatar from '@src/components/common/SpeakerAvatar';
import { useWorkspaceSpeakerFormat } from '@src/hooks/useWorkspaceSpeakerFormat';
import { getSpeakerColor } from '@src/services/SpeakerManagementService';
import CheckIconAlt from '@src/svgs/CheckIconAlt';
import { formatSpeakerRole } from '@src/utility/speakerRole';
import { getSpeakerColorFromLabel } from '@src/utility/speakerUtils';
import { observer } from 'mobx-react-lite';
import React from 'react';
import styled from 'styled-components';

import type { Speaker } from './SpeakerTypes';

interface SpeakerSelectorProps {
  speakers: Speaker[];
  selectedSpeakers: Speaker[];
  onChange: (selectedSpeakers: Speaker[]) => void;
}

const SpeakerSelector: React.FC<SpeakerSelectorProps> = ({
  speakers,
  selectedSpeakers,
  onChange,
}) => {
  // Each speaker's Title line reads as on their nametag (PLAT-12261).
  const speakerFormat = useWorkspaceSpeakerFormat();
  const toggleSpeaker = (speakerLabel: string) => {
    const selectedSpeakerLabels = selectedSpeakers.map(
      speaker => speaker.speakerLabel,
    );
    if (selectedSpeakerLabels.includes(speakerLabel)) {
      onChange(
        selectedSpeakers.filter(
          speaker => speaker.speakerLabel !== speakerLabel,
        ),
      );
    } else {
      const found = speakers.find(s => s.speakerLabel === speakerLabel);
      if (found) {
        onChange([...selectedSpeakers, found]);
      }
    }
  };

  return (
    <SpeakerSelectorContainer>
      <SpeakerList>
        {speakers.map((speaker, index) => {
          // Check if speaker is selected by comparing speakerLabel
          const isSelected = selectedSpeakers.some(
            s => s.speakerLabel === speaker.speakerLabel,
          );

          const speakerTestId = speaker.isAnonymous
            ? `speaker-${speaker.speakerLabel}`
            : `speaker-${speaker.name?.toLowerCase().replace(/\s+/g, '-')}`;

          // Use getSpeakerColor (DatoCMS ID hash) for assigned speakers
          // to match the Speakers panel colors exactly
          const color =
            !speaker.isAnonymous && speaker.id
              ? getSpeakerColor(speaker.id, index)
              : getSpeakerColorFromLabel(speaker.speakerLabel);

          return (
            <SpeakerItem
              key={speaker.speakerLabel}
              selected={isSelected}
              data-testid={speakerTestId}
              data-selected={isSelected}
              onClick={() => toggleSpeaker(speaker.speakerLabel)}
            >
              <CheckBox
                isChecked={isSelected}
                data-testid={`${speakerTestId}-checkbox`}
              >
                {isSelected && <CheckIconAlt />}
              </CheckBox>
              <SpeakerAvatar
                name={
                  speaker.isAnonymous
                    ? `Speaker ${speaker.speakerLabel.replace('spk_', '')}`
                    : speaker.name || '?'
                }
                color={color}
                headshotUrl={speaker.headshots?.[0]}
                size={24}
              />
              <SpeakerInfo>
                {speaker.isAnonymous ? (
                  <SpeakerName $color={color}>
                    Speaker {speaker.speakerLabel.replace('spk_', '')}
                  </SpeakerName>
                ) : (
                  <>
                    <SpeakerName $color={color}>{speaker.name}</SpeakerName>
                    {formatSpeakerRole(speaker, speakerFormat) && (
                      <SpeakerTitle>
                        {formatSpeakerRole(speaker, speakerFormat)}
                      </SpeakerTitle>
                    )}
                  </>
                )}
              </SpeakerInfo>
            </SpeakerItem>
          );
        })}
      </SpeakerList>
    </SpeakerSelectorContainer>
  );
};

// Styled components
const SpeakerSelectorContainer = styled.div``;

const SpeakerList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 192px;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-width: thin;
  scrollbar-gutter: stable;
`;

const SpeakerItem = styled.div<{ selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 8px;
  background: var(--surface);
  border: 1px solid var(--border);
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: var(--surface-elevated);
    border-color: var(--border);
  }
`;

const CheckBox = styled.div<{ isChecked: boolean }>`
  outline: 0;
  background-color: ${props =>
    props.isChecked ? 'var(--accent)' : 'transparent'};
  width: 16px;
  height: 16px;
  border-radius: 2px;
  overflow: hidden;
  border: 1px solid
    ${props => (props.isChecked ? 'var(--accent)' : 'var(--text-muted)')};
  box-sizing: border-box;
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const SpeakerInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const SpeakerName = styled.span<{ $color?: string }>`
  font-weight: 500;
  font-size: 12px;
  line-height: 1.3;
  color: ${props => props.$color || 'var(--foreground)'};
`;

const SpeakerTitle = styled.span`
  font-size: 12px;
  line-height: 1.3;
  color: var(--text-muted);
`;

export default observer(SpeakerSelector);
